import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { FiltersStatus } from './SharedFiltersProvider';
import { ShepherdRisk } from '../../oe-report/sections/shepherdRisksData';
import { useShepherdRisksData } from '../hooks/useShepherdRisksData';

export interface ShepherdRisksContextValue {
  finTechOpsShepherdRisksStatus: FiltersStatus;
  finTechOpsShepherdRisks: ShepherdRisk[];
}

const EMPTY: ShepherdRisk[] = [];

const defaultValue: ShepherdRisksContextValue = {
  finTechOpsShepherdRisksStatus: 'loading',
  finTechOpsShepherdRisks: EMPTY
};

const ShepherdRisksContext = createContext<ShepherdRisksContextValue>(defaultValue);

export const useShepherdRisks = () => useContext(ShepherdRisksContext);

interface Props {
  children: React.ReactNode;
}

export const ShepherdRisksProvider = ({ children }: Props) => {
  const { data, loading, error } = useShepherdRisksData();
  const [risks, setRisks] = useState<ShepherdRisk[]>(EMPTY);
  const [status, setStatus] = useState<FiltersStatus>('loading');

  useEffect(() => {
    if (loading) {
      setStatus('loading');
      return;
    }
    if (error) {
      // The hook already logs the failure; just reflect it as status here. §6c shows an
      // error state, and the rest of §6 (Risk Remediator) is independent of this feed.
      setStatus('error');
      return;
    }
    if (data) {
      setRisks(data);
      setStatus('finished');
    }
  }, [loading, error, data]);

  const value = useMemo<ShepherdRisksContextValue>(
    () => ({ finTechOpsShepherdRisksStatus: status, finTechOpsShepherdRisks: risks }),
    [status, risks]
  );

  return <ShepherdRisksContext.Provider value={value}>{children}</ShepherdRisksContext.Provider>;
};
